/**
 * Framework presentation helpers (G2-02, HG-2A).
 *
 * Pure functions over a ResolvedFramework that encode the Guide Change
 * Specification presentation rules (GCH-04, GCH-05, GCH-06, GCH-21) once,
 * for every surface (web, mobile, authority exports):
 *
 *  - the WingMentor working label with the ICAO title and Framework Listing
 *    Order number available on detail and hover (GCH-05);
 *  - behaviour text and ICAO OB reference read from the registry at run
 *    time (GCH-06);
 *  - derived behaviours beneath their parent with the parent code shown,
 *    operator-competency behaviours under the Operator Competency with no
 *    parent (GCH-21).
 */
import { FRAMEWORK_LISTING_ORDER_V1 } from '../icd/enums';
import type {
  ResolvedBehaviour,
  ResolvedCompetency,
  ResolvedFramework,
} from './loader';

/** The registry marker for WingMentor-added (non-ICAO) content. */
const WINGMENTOR_ADDED_REF = 'WingMentor-added';

/** Working label: the WingMentor label, falling back to the ICAO title (GCH-05). */
export function competencyLabel(competency: ResolvedCompetency): string {
  return competency.wmLabel ?? competency.title;
}

/**
 * Detail line: the authoritative ICAO title with the competency's Framework
 * Listing Order number (GCH-05), e.g. "1. Application of knowledge".
 */
export function competencyDetail(competency: ResolvedCompetency): string {
  return `${competency.listingPosition}. ${competency.title}`;
}

/**
 * WingMentor-added status (GCH-04): true when every behaviour of the
 * competency carries the WingMentor-added marker instead of an ICAO OB
 * reference (KNO in the reference model; Doc 9868 4.3.2 is the basis).
 */
export function isWingMentorAdded(competency: ResolvedCompetency): boolean {
  return (
    competency.behaviours.length > 0 &&
    competency.behaviours.every((b) => b.icaoRef === WINGMENTOR_ADDED_REF)
  );
}

/**
 * Behaviour reference for presentation (GCH-06): the code with its ICAO OB
 * reference where one exists, e.g. "PRO-1 (OB 1.1)"; a derived behaviour
 * shows its parent instead, e.g. "OPR-KNO-1A (from KNO-1)" (GCH-21).
 */
export function behaviourReference(behaviour: ResolvedBehaviour): string {
  if (behaviour.origin === 'operator_derived' && behaviour.parentCode) {
    return `${behaviour.code} (from ${behaviour.parentCode})`;
  }
  if (behaviour.icaoRef && behaviour.icaoRef !== WINGMENTOR_ADDED_REF) {
    return `${behaviour.code} (${behaviour.icaoRef})`;
  }
  return behaviour.code;
}

/** One display row of a competency's behaviour list (GCH-21 grouping). */
export interface BehaviourDisplayRow {
  behaviour: ResolvedBehaviour;
  /** Nesting depth: 0 for top-level rows, 1 for derived rows under a parent. */
  depth: 0 | 1;
  /** The parent code a derived row renders beneath (GCH-21), else null. */
  parentCode: string | null;
}

/**
 * Order a competency's behaviours for display (GCH-21): reference and
 * operator-competency behaviours in their registry order, each derived
 * behaviour immediately beneath its parent with the parent code shown.
 * A derived behaviour whose parent is not in the list (cross-version
 * anchor) falls back to a top-level row that still shows its parent code.
 */
export function groupBehavioursForDisplay(
  competency: ResolvedCompetency,
): BehaviourDisplayRow[] {
  const derivedByParent = new Map<string, ResolvedBehaviour[]>();
  const topLevel: ResolvedBehaviour[] = [];
  const orphanedDerived: ResolvedBehaviour[] = [];

  for (const b of competency.behaviours) {
    if (b.origin === 'operator_derived' && b.parentCode) {
      const list = derivedByParent.get(b.parentCode);
      if (list) list.push(b);
      else derivedByParent.set(b.parentCode, [b]);
    } else {
      topLevel.push(b);
    }
  }

  const rows: BehaviourDisplayRow[] = [];
  for (const parent of topLevel) {
    rows.push({ behaviour: parent, depth: 0, parentCode: null });
    const children = derivedByParent.get(parent.code);
    if (children) {
      for (const child of children) {
        rows.push({ behaviour: child, depth: 1, parentCode: parent.code });
      }
      derivedByParent.delete(parent.code);
    }
  }
  // Derived rows whose parent is not among this competency's rows.
  for (const children of derivedByParent.values()) {
    for (const child of children) orphanedDerived.push(child);
  }
  for (const child of orphanedDerived) {
    rows.push({ behaviour: child, depth: 0, parentCode: child.parentCode });
  }
  return rows;
}

/** Look up one behaviour by code across the whole framework. */
export function findBehaviour(
  framework: ResolvedFramework,
  code: string,
): ResolvedBehaviour | undefined {
  for (const competency of framework.competencies) {
    const found = competency.behaviours.find((b) => b.code === code);
    if (found) return found;
  }
  return undefined;
}

/** Look up one competency by code. */
export function findCompetency(
  framework: ResolvedFramework,
  code: string,
): ResolvedCompetency | undefined {
  return framework.competencies.find((c) => c.code === code);
}

/**
 * Order anything keyed by competency code into the Framework Listing Order
 * (ICD 8.11, GAT-02: "every competency list in the product ... with the
 * Operator Competency last").
 *
 * The rule was already honoured by the registry, the loader, the presentation
 * helpers above and every export, and it was still possible for a screen to
 * list competencies in whatever order its query returned. The Annex C capture
 * run of 19 August 2026 photographed exactly that: the Step 6 selector read
 * COM, FPA, FPM, KNO because the page asked PostgREST for
 * `.order('competency')`. A list is a list whether it is a report row or a
 * control an instructor grades through, so the ordering rule lives here, once,
 * and a surface adopts it by calling this rather than by sorting its own way.
 *
 * `listingOrder` defaults to the reference order and is meant to be passed the
 * resolved version's own `listingOrder` where the caller has one: an operator
 * version puts its Operator Competency last, and that order is the version's,
 * not this module's.
 *
 * A code the order does not name sorts last rather than disappearing. Dropping
 * a row the instructor graded would be a worse failure than showing it in an
 * unexpected place, and the sort is stable, so codes that tie keep the order
 * they arrived in.
 */
export function sortByFrameworkListingOrder<T>(
  rows: readonly T[],
  competencyOf: (row: T) => string,
  listingOrder: readonly string[] = FRAMEWORK_LISTING_ORDER_V1,
): T[] {
  const positionOf = (code: string): number => {
    const index = listingOrder.indexOf(code);
    return index === -1 ? listingOrder.length : index;
  };
  return [...rows].sort((a, b) => positionOf(competencyOf(a)) - positionOf(competencyOf(b)));
}
